import { useEffect, useState } from "react";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import { BiSolidError } from "react-icons/bi";
import { FaPlus, FaFileInvoiceDollar } from "react-icons/fa";
import { FiEdit } from "react-icons/fi";
import { IoCloseSharp } from "react-icons/io5";
import { MdOutlineEdit } from "react-icons/md";
import "react-loading-skeleton/dist/skeleton.css";
import { NavLink, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useDebounce } from "use-debounce";
import ActionMenu from "../../../component/ActionMenu";
import SelectBox from "../../../component/selectbox/SelectBox";
import SkeletonLoading from "../../../component/SkeletonLoading";
import {
  useDeleteRecordMutation,
  useGetAllParentsQuery,
} from "../../../store/api/school/PraentSlice";
import { useCancelParentReservationsMutation } from "../../../store/api/school/ReservationSlice";
import {
  useGetParentStudentsQuery,
  useRecordExternalPaymentMutation,
} from "../../../store/api/admin/paymentSlice";

import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import AdminPagination from "../../../component/pagination/AdminPagination";
import Tooltip from "../../../component/Tooltip";
import i18n from "../../../i8next";
import { capitalizeString } from "../../../utils/dateAndTimeConversion";

const initialActions = {
  search: "",
};

const initialPagination = {
  limit: 10,
  page: 1,
  total: "",
  totalPages: "",
};

const ParentsList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { slug } = useParams();
  const { user } = useSelector((state) => state.UserReducer);
  const role = user?.role === "super_admin" ? "admin" : user?.role;
  const isAdmin = role === "admin";

  // slug base fetch data
  const userRole =
    slug === "school-parents"
      ? "school"
      : slug === "vendor-parents"
        ? "vendor"
        : null;

  const [show, setShow] = useState(false);
  const [id, setId] = useState("");
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancelTarget, setCancelTarget] = useState({ id: null, name: "" });

  const initialExternalPaymentForm = {
    studentIds: [],
    amount: "",
    paymentType: "full_payment",
    paymentMethod: "bank_wire",
    referenceNumber: "",
    notes: "",
    receiptFile: null,
  };
  const [showExternalPaymentModal, setShowExternalPaymentModal] =
    useState(false);
  const [externalPaymentTarget, setExternalPaymentTarget] = useState(null);
  const [externalPaymentForm, setExternalPaymentForm] = useState(
    initialExternalPaymentForm,
  );
  const [errors, setErrors] = useState({});

  const { data: parentStudentsData, isFetching: isFetchingParentStudents } =
    useGetParentStudentsQuery(externalPaymentTarget?.id, {
      skip: !externalPaymentTarget,
    });
  const parentStudents = parentStudentsData?.data || [];

  const [recordExternalPayment, { isLoading: isRecordingExternalPayment }] =
    useRecordExternalPaymentMutation();
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebounce(search, 300);
  const [parents, setParents] = useState([]);
  const [action, setAction] = useState(initialActions);
  const [pagination, setPagination] = useState(initialPagination);

  const { data, isSuccess, isLoading, isFetching, refetch } =
    useGetAllParentsQuery({
      page: pagination.page,
      limit: pagination.limit,
      ...action,
      role: userRole,
    });

  // delete query
  const [deleteRecord, { isLoading: isDeleteLoading }] =
    useDeleteRecordMutation();

  const [cancelParentReservations, { isLoading: isCancelLoading }] =
    useCancelParentReservationsMutation();

  useEffect(() => {
    if (isSuccess && data?.data) {
      setParents(data.data.parents);
      setPagination(data?.data?.pagination);
    }
  }, [data, isSuccess]);

  // delete function
  const handleDelte = async () => {
    toast.dismiss();
    try {
      const response = await deleteRecord(`${id}`).unwrap();
      if (response.statusCode === 200) {
        toast.success(response.message);
        setShow(false);
        setId("");
        refetch(); // Refresh the list after deletion
      }
    } catch (err) {
      const mesg = err.data?.message || t("something_went_wrong");
      toast.error(mesg);
    }
  };

  // search State & Debounce Setup
  useEffect(() => {
    if (debouncedSearch) {
      setPagination(() => ({ limit: 10, page: 1 }));
      setAction((prev) => ({ ...prev, search }));
    }
  }, [debouncedSearch]);

  useEffect(() => {
    if (!search.trim()) {
      // setPagination((prev) => ({ limit: 10, page: 1 }));
      setAction((prev) => ({ ...prev, search: "" }));
    }
  }, [search]);

  const handlePageChange = (page) => {
    setPagination((prev) => ({ ...prev, page }));
  };

  const handleLimitChange = (limit) => {
    setPagination((prev) => ({
      ...prev,
      limit,
      page: 1,
    }));
  };

  const handleResetFilters = () => {
    setAction(initialActions);
    setPagination((prev) => ({
      ...prev,
      limit: 10,
      page: 1,
    }));
    setSearch("");
  };

  const handleClose = () => setShow(false);

  const handleOpenExternalPayment = (item) => {
    setExternalPaymentTarget({
      id: item.id,
      email: item.email,
      full_name: item.full_name,
    });
    setExternalPaymentForm(initialExternalPaymentForm);
    setShowExternalPaymentModal(true);
  };

  const handleCloseExternalPayment = () => {
    setShowExternalPaymentModal(false);
    setExternalPaymentTarget(null);
    setExternalPaymentForm(initialExternalPaymentForm);
    setErrors({});
  };

  const validateExternalPaymentForm = () => {
    const newErrors = {};
    const { studentIds, amount } = externalPaymentForm;

    if (studentIds.length === 0) {
      newErrors.studentIds = t("external_payment_select_student");
    }
    if (!amount || Number(amount) <= 0) {
      newErrors.amount = t("external_payment_invalid_amount");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmitExternalPayment = async () => {
    if (!validateExternalPaymentForm()) return;
    toast.dismiss();
    const { studentIds, amount, paymentType, paymentMethod, receiptFile } =
      externalPaymentForm;

    const formData = new FormData();
    formData.append("parentId", externalPaymentTarget.id);
    formData.append("studentIds", JSON.stringify(studentIds));
    formData.append("amount", amount);
    formData.append("paymentType", paymentType);
    formData.append("paymentMethod", paymentMethod);
    formData.append("referenceNumber", externalPaymentForm.referenceNumber);
    formData.append("notes", externalPaymentForm.notes);
    if (receiptFile) formData.append("receipt_document", receiptFile);

    try {
      const response = await recordExternalPayment(formData).unwrap();
      if (response.statusCode === 201) {
        toast.success(response.message);
        handleCloseExternalPayment();
        // recordExternalPayment lives in a separate RTK Query API instance
        // (paymentSlice) from useGetAllParentsQuery (PraentSlice) — its
        // invalidatesTags can't refresh this table, so refetch directly.
        refetch();
      }
    } catch (err) {
      const mesg = err.data?.message || t("something_went_wrong");
      toast.error(mesg);
    }
  };

  const handleCancelParentReservations = async () => {
    toast.dismiss();
    try {
      const response = await cancelParentReservations(cancelTarget.id).unwrap();
      toast.success(response.message || t("reservations_cancelled_success"));
      setShowCancelConfirm(false);
      setCancelTarget({ id: null, name: "" });
    } catch (err) {
      toast.error(err.data?.message || t("something_went_wrong"));
    }
  };

  // Language Change Handler
  useEffect(() => {
    const h = () => {
      refetch?.();
    };
    i18n.on("languageChanged", h);
    return () => i18n.off("languageChanged", h);
  }, [refetch]);

  return (
    <>
      <div className="d-flex justify-content-between align-items-center flex-wrap w-100 mb-2">
        <div className="total">
          {t("total")} {t("parents")}: {pagination?.total}
        </div>

        <div className="d-flex flex-wrap gap-2 ms-auto">
          <div className="d-flex flex-grow-1">
            <input
              type="text"
              className="form-control custom-field-one"
              placeholder={`${t("search")}..`}
              value={search}
              onChange={(e) => setSearch(e.target.value.trim())}
            />
          </div>

          <div className="d-flex gap-2 ms-auto">
            <button
              className="btn btn-primary addDriver"
              onClick={handleResetFilters}
            >
              {t("btns.reset")}
            </button>

            {!isAdmin && (
              <NavLink
                to="add"
                className="page-add-btn d-flex align-items-center justify-content-center flex-grow-1 gap-2"
              >
                <FaPlus /> {t("add")} {t("parent")}
              </NavLink>
            )}
          </div>
        </div>
      </div>

      <div className="table m-0">
        <table>
          <thead>
            <tr>
              <th className="text-nowrap">{`${t("Sr")}. ${t("no")}.`}</th>
              <th className="text-nowrap">
                {t("parent")} {t("name")}
              </th>
              <th>{t("email")}</th>
              <th
                className="text-nowrap"
                style={{ minWidth: "400px", width: "auto" }}
              >
                {t("address")}
              </th>
              <th>
                {t("mobile")} {t("no")}.
              </th>
              <th>{t("status")}</th>

              <th>{t("action")}</th>
            </tr>
          </thead>
          <tbody>
            {isLoading || isFetching ? (
              <SkeletonLoading MainLength={10} length={7} />
            ) : parents?.length > 0 ? (
              parents?.map((item, i) => {
                const link = isAdmin
                  ? `/${slug}/${item.id}/students`
                  : `/parents/${item.id}/students`;

                return (
                  <tr key={i}>
                    <td>{(pagination.page - 1) * pagination.limit + i + 1}</td>
                    <td className="text-nowrap">
                      {/* Make parent name clickable link */}
                      <Tooltip
                        title={`${t("view")} ${item?.full_name || ""}'s ${t("students")} ${t("details")}`}
                      >
                        <NavLink
                          to={link}
                          className="text-decoration-none text-primary fw-medium"
                        >
                          {item?.full_name}
                        </NavLink>
                      </Tooltip>
                    </td>
                    <td className="text-nowrap">{item?.email}</td>
                    <td style={{ minWidth: "400px" }}>{item?.street}</td>
                    <td className="text-nowrap">{item?.phone}</td>
                    <td>
                      <span
                        className={`badge ${
                          item?.status === "payment_pending"
                            ? "bg-warning"
                            : item?.status === "pending"
                              ? "bg-secondary"
                              : "bg-success"
                        }`}
                      >
                        {item?.status ? capitalizeString(item?.status) : "N/A"}
                      </span>
                    </td>
                    {!isAdmin && (
                      <td>
                        <ActionMenu
                          actions={[
                            {
                              label: t("edit"),
                              icon: <MdOutlineEdit size={16} />,
                              onClick: () =>
                                navigate(`/parents/edit/${item.id}`),
                            },
                          ]}
                        />
                      </td>
                    )}
                    {isAdmin && (
                      <td>
                        <ActionMenu
                          actions={[
                            {
                              label: t("record_external_payment"),
                              icon: <FaFileInvoiceDollar size={14} />,
                              onClick: () => handleOpenExternalPayment(item),
                            },
                            {
                              label: t("cancel_reservations"),
                              icon: <IoCloseSharp size={15} />,
                              onClick: () => {
                                setCancelTarget({
                                  id: item.id,
                                  name: item.full_name || item.email,
                                });
                                setShowCancelConfirm(true);
                              },
                              danger: true,
                            },
                          ]}
                        />
                      </td>
                    )}
                  </tr>
                );
              })
            ) : (
              <tr className="text-center">
                <td colSpan="7">
                  <div>
                    <span>
                      {t("no")} {t("parent")} {t("found")}
                    </span>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <AdminPagination
        totalPages={pagination.totalPages}
        currentPage={pagination.page}
        limit={pagination.limit}
        onPageChange={handlePageChange}
        onLimitChange={handleLimitChange}
      />

      <Modal show={show}>
        <Button className="closePopup" onClick={handleClose}>
          <IoCloseSharp />
        </Button>
        <div className="popupBox p-3 p-md-5">
          <div>
            <div className="text-center mb-3 errorIcon">
              <BiSolidError />
            </div>
            <div className="text-center mb-4 deleteHd">{t("delete-modal")}</div>
            <div className="text-center">
              <Button className="smallBtn cancel me-3" onClick={handleClose}>
                {t("btns.cancel")}
              </Button>
              <Button className="smallBtn auto" onClick={handleDelte}>
                {isDeleteLoading ? (
                  <div className="spinner-border" role="status">
                    <span className="sr-only p-0"></span>
                  </div>
                ) : (
                  t("delete")
                )}
              </Button>
            </div>
          </div>
        </div>
      </Modal>

      {/* Cancel Parent Reservations Confirm Modal */}
      <Modal show={showCancelConfirm} centered>
        <Button
          className="closePopup"
          onClick={() => setShowCancelConfirm(false)}
        >
          <IoCloseSharp />
        </Button>
        <div className="popupBox p-3 p-md-5">
          <div className="text-center mb-3 errorIcon">
            <BiSolidError />
          </div>
          <div className="text-center mb-2 deleteHd">
            {t("cancel_reservations")}
          </div>
          {cancelTarget.name && (
            <div className="text-center mb-2" style={{ fontSize: "0.9rem" }}>
              <strong>{cancelTarget.name}</strong>
            </div>
          )}
          <div
            className="mb-4 p-2 text-center"
            style={{
              background: "#fff3cd",
              border: "1px solid #ffc107",
              borderRadius: "8px",
              fontSize: "0.82rem",
              color: "#856404",
            }}
          >
            ⚠️ {t("cancel_reservations_warning")}
          </div>
          <div className="text-center">
            <Button
              className="smallBtn cancel me-3"
              onClick={() => setShowCancelConfirm(false)}
              disabled={isCancelLoading}
            >
              {t("btns.cancel")}
            </Button>
            <Button
              className="smallBtn auto"
              onClick={handleCancelParentReservations}
              disabled={isCancelLoading}
            >
              {isCancelLoading ? (
                <div className="spinner-border" role="status">
                  <span className="sr-only p-0"></span>
                </div>
              ) : (
                t("yes_cancel")
              )}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        show={showExternalPaymentModal}
        onHide={handleCloseExternalPayment}
        size="lg"
        centered
        backdrop="static"
        className="detailModel"
      >
        <Modal.Header closeButton className="border-0">
          <Modal.Title className="h5">
            {t("record_external_payment")}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body
          style={{
            maxHeight: "80vh",
            overflowY: "auto",
          }}
        >
          <div className="popupBox">
            <div className="text-center mb-3" style={{ fontSize: "0.9rem" }}>
              <strong>
                {externalPaymentTarget?.full_name ||
                  externalPaymentTarget?.email}
              </strong>
            </div>

            <div className="mb-3">
              <label className="form-label" style={{ fontSize: "0.85rem" }}>
                {t("external_payment_select_students")}{" "}
                <span className="text-danger">*</span>
              </label>
              {parentStudents.length === 0 && !isFetchingParentStudents ? (
                <div className="text-muted" style={{ fontSize: "0.85rem" }}>
                  {t("no")} {t("students")} {t("found")}
                </div>
              ) : (
                <SelectBox
                  isMulti
                  isLoading={isFetchingParentStudents}
                  name="studentIds"
                  val={externalPaymentForm.studentIds}
                  options={parentStudents.map((student) => ({
                    value: student.student_id,
                    label: `${student.full_name}${
                      student.fee ? ` ($${student.fee})` : ""
                    }`,
                  }))}
                  change={(name, value) => {
                    setExternalPaymentForm((prev) => ({
                      ...prev,
                      [name]: value,
                    }));
                    setErrors((prev) => ({ ...prev, studentIds: "" }));
                  }}
                />
              )}
              {errors.studentIds && (
                <span className="text-danger small">{errors.studentIds}</span>
              )}
            </div>

            <div className="row g-3 mb-3">
              <div className="col-6">
                <label className="form-label" style={{ fontSize: "0.85rem" }}>
                  {t("amount")} <span className="text-danger">*</span>
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  className="form-control"
                  value={externalPaymentForm.amount}
                  onChange={(e) => {
                    setExternalPaymentForm((prev) => ({
                      ...prev,
                      amount: e.target.value,
                    }));
                    setErrors((prev) => ({ ...prev, amount: "" }));
                  }}
                />
                {errors.amount && (
                  <span className="text-danger small">{errors.amount}</span>
                )}
              </div>
              <div className="col-6">
                <label className="form-label" style={{ fontSize: "0.85rem" }}>
                  {t("external_payment_type")}{" "}
                  <span className="text-danger">*</span>
                </label>
                <select
                  className="form-select"
                  value={externalPaymentForm.paymentType}
                  onChange={(e) =>
                    setExternalPaymentForm((prev) => ({
                      ...prev,
                      paymentType: e.target.value,
                    }))
                  }
                >
                  <option value="full_payment">
                    {t("external_payment_type_full")}
                  </option>
                  <option value="partial_payment">
                    {t("external_payment_type_partial")}
                  </option>
                  <option value="deposit">
                    {t("external_payment_type_deposit")}
                  </option>
                </select>
              </div>
            </div>

            <div className="row g-3 mb-3">
              <div className="col-6">
                <label className="form-label" style={{ fontSize: "0.85rem" }}>
                  {t("external_payment_method")}{" "}
                  <span className="text-danger">*</span>
                </label>
                <select
                  className="form-select"
                  value={externalPaymentForm.paymentMethod}
                  onChange={(e) =>
                    setExternalPaymentForm((prev) => ({
                      ...prev,
                      paymentMethod: e.target.value,
                    }))
                  }
                >
                  <option value="bank_wire">
                    {t("external_payment_method_bank_wire")}
                  </option>
                  <option value="cash">
                    {t("external_payment_method_cash")}
                  </option>
                  <option value="cheque">
                    {t("external_payment_method_cheque")}
                  </option>
                  <option value="other">
                    {t("external_payment_method_other")}
                  </option>
                </select>
              </div>
              <div className="col-6">
                <label className="form-label" style={{ fontSize: "0.85rem" }}>
                  {t("external_payment_reference_number")}
                </label>
                <input
                  type="text"
                  className="form-control"
                  value={externalPaymentForm.referenceNumber}
                  onChange={(e) =>
                    setExternalPaymentForm((prev) => ({
                      ...prev,
                      referenceNumber: e.target.value,
                    }))
                  }
                />
              </div>
            </div>

            {/* <div className="mb-3">
              <label className="form-label" style={{ fontSize: "0.85rem" }}>
                {t("external_payment_receipt")}
              </label>
              <input
                type="file"
                className="form-control"
                accept="image/*,application/pdf"
                onChange={(e) =>
                  setExternalPaymentForm((prev) => ({
                    ...prev,
                    receiptFile: e.target.files?.[0] || null,
                  }))
                }
              />
            </div> */}

            <div className="mb-3">
              <label className="form-label" style={{ fontSize: "0.85rem" }}>
                {t("external_payment_notes")}
              </label>
              <textarea
                className="form-control"
                rows={2}
                style={{ resize: "none" }}
                value={externalPaymentForm.notes}
                onChange={(e) =>
                  setExternalPaymentForm((prev) => ({
                    ...prev,
                    notes: e.target.value,
                  }))
                }
              />
            </div>

            <div className="text-center">
              <Button
                className="smallBtn cancel me-3"
                onClick={handleCloseExternalPayment}
                disabled={isRecordingExternalPayment}
              >
                {t("btns.cancel")}
              </Button>
              <Button
                className="smallBtn auto"
                onClick={handleSubmitExternalPayment}
                disabled={isRecordingExternalPayment}
              >
                {isRecordingExternalPayment ? (
                  <div
                    className="spinner-border spinner-border-sm"
                    role="status"
                  />
                ) : (
                  t("btns.save")
                )}
              </Button>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};
export default ParentsList;
